import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Hammer, Map as MapIcon, Calculator, Briefcase } from "lucide-react";
import { fetchServices } from "../../api/public.api.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { SEO } from "../../lib/seo.jsx";

const ICONS = {
  home: Home,
  hammer: Hammer,
  map: MapIcon,
  calculator: Calculator,
  briefcase: Briefcase,
};

export default function ServicesListPage() {
  const { data: services = [], isLoading } = useQuery({
    queryKey: ["public", "services"],
    queryFn: fetchServices,
    staleTime: 5 * 60_000,
  });

  return (
    <>
      <SEO
        title="Services"
        description="Custom homes, knockdown rebuild, house & land packages, and financial assistance — bundled into a single managed build."
      />

      <PageHero
        eyebrow="What we do"
        title="Services"
        description="From a brand-new custom build on bare land to a knockdown rebuild on your existing block — every option is project-managed by one team."
      />

      <Container className="py-16 lg:py-20">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-64 animate-pulse rounded-2xl bg-bg-elevated" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {services.map((s) => {
              const Icon = ICONS[s.icon] ?? Home;
              return (
                <Link
                  key={s._id ?? s.slug}
                  to={`/services/${s.slug}`}
                  className="group flex flex-col rounded-2xl border border-border-subtle bg-bg-elevated p-7 transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-card-hover"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent/15 text-accent">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h2 className="mt-5 font-display text-2xl text-text-primary lg:text-3xl">
                    {s.title}
                  </h2>
                  {s.shortDescription && (
                    <p className="mt-3 text-text-secondary">{s.shortDescription}</p>
                  )}
                  <span className="mt-auto pt-6 inline-flex items-center gap-1 text-sm text-accent transition-transform group-hover:translate-x-1">
                    Learn more <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </Container>
    </>
  );
}
